/**
 * Context Calendar Component
 * ===========================
 * 
 * Visual calendar view showing context events with multi-day spans,
 * event details on hover, and quick actions.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Plus,
  Repeat,
  Target,
  Store,
  Globe,
  Calendar as CalendarIcon,
} from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

interface ContextEvent {
  id: string;
  event_type: string;
  event_name: string;
  description: string | null;
  start_date: string;
  end_date: string | null;
  is_recurring?: boolean;
  expected_impact_direction: 'up' | 'down' | 'mixed' | null;
  expected_impact_pct_low: number | null;
  expected_impact_pct_high: number | null;
  affected_channels: string[];
  affected_platforms: string[];
  confidence: number;
}

interface Props {
  events: ContextEvent[];
  onEventClick: (event: ContextEvent) => void;
  onAddEvent?: (date: Date) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function formatDateKey(date: Date): string {
  return date.toISOString().split('T')[0];
}

function isDateInRange(date: Date, start: string, end: string | null): boolean {
  const dateStr = formatDateKey(date);
  if (dateStr < start) return false;
  if (end && dateStr > end) return false;
  return true;
}

function isStartOfEvent(date: Date, event: ContextEvent): boolean {
  return formatDateKey(date) === event.start_date;
}

function isEndOfEvent(date: Date, event: ContextEvent): boolean {
  if (!event.end_date) return formatDateKey(date) === event.start_date;
  return formatDateKey(date) === event.end_date;
}

// =============================================================================
// EVENT TOOLTIP
// =============================================================================

interface TooltipProps {
  event: ContextEvent;
  position: { x: number; y: number };
}

const EventTooltip: React.FC<TooltipProps> = ({ event, position }) => {
  const impactText = event.expected_impact_pct_low && event.expected_impact_pct_high
    ? `${event.expected_impact_pct_low > 0 ? '+' : ''}${event.expected_impact_pct_low}% to ${event.expected_impact_pct_high > 0 ? '+' : ''}${event.expected_impact_pct_high}%`
    : 'Not specified';

  const typeLabels: Record<string, string> = {
    internal_promo: 'Internal Promo',
    retailer_promo: 'Retailer Promo',
    competitor_activity: 'Competitor Activity',
    industry_event: 'Industry Event',
    seasonality: 'Seasonality',
    custom: 'Custom Event',
  };

  return (
    <div 
      className="fixed z-50 bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-3 w-64 pointer-events-none"
      style={{ 
        left: Math.min(position.x, window.innerWidth - 280), 
        top: position.y + 10,
      }}
    >
      <div className="font-semibold text-white mb-1">{event.event_name}</div>
      <div className="text-xs text-gray-400 mb-2">{typeLabels[event.event_type]}</div>
      
      {event.description && (
        <p className="text-xs text-gray-300 mb-2 line-clamp-2">{event.description}</p>
      )}
      
      <div className="space-y-1 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-gray-400">Impact:</span>
          <span className={`font-medium ${
            event.expected_impact_direction === 'up' ? 'text-emerald-400' :
            event.expected_impact_direction === 'down' ? 'text-rose-400' :
            'text-amber-400'
          }`}>
            {event.expected_impact_direction?.toUpperCase() || 'N/A'} ({impactText})
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-400">Confidence:</span>
          <span className="text-white">{Math.round(event.confidence * 100)}%</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-400">Dates:</span>
          <span className="text-white">
            {event.start_date}{event.end_date && event.end_date !== event.start_date ? ` → ${event.end_date}` : ''}
          </span>
        </div>
        {event.affected_platforms && event.affected_platforms.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Platforms:</span>
            <span className="text-white capitalize">{event.affected_platforms.join(', ')}</span>
          </div>
        )}
      </div>
      
      {event.is_recurring && (
        <div className="flex items-center gap-1 mt-2 text-xs text-purple-400">
          <Repeat className="w-3 h-3" />
          Recurring event
        </div>
      )}
    </div>
  );
};

// =============================================================================
// COMPONENT
// =============================================================================

const ContextCalendar: React.FC<Props> = ({ events, onEventClick, onAddEvent }) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [hoveredEvent, setHoveredEvent] = useState<{ event: ContextEvent; position: { x: number; y: number } } | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  
  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  const goToToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };
  
  // Build calendar grid
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(currentYear, currentMonth, day));
    }
    
    // Pad to complete the final week
    while (days.length % 7 !== 0) {
      days.push(null);
    }
    
    return days;
  }, [currentYear, currentMonth]);
  
  // Map events to dates
  const eventsByDate = useMemo(() => {
    const map = new Map<string, ContextEvent[]>();
    
    for (const event of events) {
      // For each day in the calendar, check if event overlaps
      for (const day of calendarDays) {
        if (!day) continue;
        
        if (isDateInRange(day, event.start_date, event.end_date)) {
          const key = formatDateKey(day);
          if (!map.has(key)) map.set(key, []);
          map.get(key)!.push(event);
        }
      }
    }
    
    return map;
  }, [events, calendarDays]);
  
  const getEventColor = (event: ContextEvent): { bg: string; border: string; text: string } => {
    const colors: Record<string, { bg: string; border: string; text: string }> = {
      internal_promo: { bg: 'bg-purple-500/20', border: 'border-purple-500', text: 'text-purple-300' },
      retailer_promo: { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-300' },
      competitor_activity: { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-300' },
      industry_event: { bg: 'bg-cyan-500/20', border: 'border-cyan-500', text: 'text-cyan-300' },
      seasonality: { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-300' },
      custom: { bg: 'bg-gray-500/20', border: 'border-gray-500', text: 'text-gray-300' },
    };
    return colors[event.event_type] || colors.custom;
  };
  
  const getImpactIcon = (direction: string | null) => {
    if (direction === 'up') return <TrendingUp className="w-3 h-3 text-emerald-400" />;
    if (direction === 'down') return <TrendingDown className="w-3 h-3 text-rose-400" />;
    if (direction === 'mixed') return <Minus className="w-3 h-3 text-amber-400" />;
    return null;
  };
  
  const isToday = (date: Date | null): boolean => {
    if (!date) return false;
    return formatDateKey(date) === formatDateKey(today);
  };
  
  const handleEventHover = useCallback((event: ContextEvent, e: React.MouseEvent) => {
    setHoveredEvent({ event, position: { x: e.clientX, y: e.clientY } });
  }, []);
  
  const handleEventLeave = useCallback(() => {
    setHoveredEvent(null);
  }, []);
  
  // Count events by type for summary
  const eventSummary = useMemo(() => {
    const counts: Record<string, number> = {};
    events.forEach(e => {
      counts[e.event_type] = (counts[e.event_type] || 0) + 1;
    });
    return counts;
  }, [events]);
  
  return (
    <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/50">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-white">
            {MONTHS[currentMonth]} {currentYear}
          </h2>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm text-amber-400 hover:bg-amber-500/10 rounded transition-colors"
          >
            Today
          </button>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Event count badges */}
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-gray-700 text-gray-300">
              {events.length} events
            </span>
            {eventSummary.industry_event > 0 && (
              <span className="px-2 py-1 rounded bg-cyan-500/20 text-cyan-400">
                {eventSummary.industry_event} industry
              </span>
            )}
            {eventSummary.retailer_promo > 0 && (
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400">
                {eventSummary.retailer_promo} retailer
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevMonth}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToNextMonth}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b border-gray-700/50 bg-gray-900/30">
        {DAYS.map(day => (
          <div key={day} className="px-2 py-3 text-center text-sm font-medium text-gray-400 uppercase tracking-wider">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          const dateKey = day ? formatDateKey(day) : '';
          const dayEvents = day ? eventsByDate.get(dateKey) || [] : [];
          const isTodayCell = isToday(day);
          const isWeekend = index % 7 === 0 || index % 7 === 6;
          
          return (
            <div
              key={index}
              className={`min-h-[120px] border-b border-r border-gray-700/30 p-1 relative group ${
                !day ? 'bg-gray-900/50' : isWeekend ? 'bg-gray-900/20' : 'hover:bg-gray-700/20'
              }`}
            >
              {day && (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <div className={`text-sm font-medium ${
                      isTodayCell 
                        ? 'w-7 h-7 flex items-center justify-center bg-amber-500 text-black rounded-full' 
                        : 'text-gray-400 px-1'
                    }`}>
                      {day.getDate()}
                    </div>
                    
                    {onAddEvent && (
                      <button
                        onClick={() => onAddEvent(day)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-amber-400 transition-all"
                        title="Add event on this day"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    {dayEvents.slice(0, 4).map((event, i) => {
                      const colors = getEventColor(event);
                      const isStart = isStartOfEvent(day, event);
                      const isEnd = isEndOfEvent(day, event);
                      
                      return (
                        <button
                          key={`${event.id}-${i}`}
                          onClick={() => onEventClick(event)}
                          onMouseEnter={(e) => handleEventHover(event, e)}
                          onMouseLeave={handleEventLeave}
                          className={`w-full text-left px-1.5 py-0.5 text-xs truncate flex items-center gap-1 ${colors.bg} ${colors.text} border-l-2 ${colors.border} hover:opacity-80 transition-opacity ${
                            isStart ? 'rounded-l' : ''
                          } ${isEnd ? 'rounded-r' : ''}`}
                        >
                          {isStart && getImpactIcon(event.expected_impact_direction)}
                          {isStart && event.is_recurring && <Repeat className="w-2.5 h-2.5 text-purple-400" />}
                          <span className="truncate">{isStart ? event.event_name : '↳'}</span>
                        </button>
                      );
                    })}
                    
                    {dayEvents.length > 4 && (
                      <div className="text-xs text-gray-500 px-1 hover:text-amber-400 cursor-pointer">
                        +{dayEvents.length - 4} more
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="px-6 py-4 border-t border-gray-700/50 bg-gray-900/30">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded border-l-2 border-purple-500 bg-purple-500/20" />
            <span className="text-gray-400">Internal Promo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded border-l-2 border-blue-500 bg-blue-500/20" />
            <span className="text-gray-400">Retailer Promo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded border-l-2 border-orange-500 bg-orange-500/20" />
            <span className="text-gray-400">Competitor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded border-l-2 border-cyan-500 bg-cyan-500/20" />
            <span className="text-gray-400">Industry Event</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded border-l-2 border-green-500 bg-green-500/20" />
            <span className="text-gray-400">Seasonal</span>
          </div>
          
          <div className="border-l border-gray-600 pl-4 flex items-center gap-4">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-emerald-400" />
              <span className="text-gray-400">Up</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingDown className="w-3 h-3 text-rose-400" />
              <span className="text-gray-400">Down</span>
            </div>
            <div className="flex items-center gap-1">
              <Repeat className="w-3 h-3 text-purple-400" />
              <span className="text-gray-400">Recurring</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tooltip */}
      {hoveredEvent && <EventTooltip event={hoveredEvent.event} position={hoveredEvent.position} />}
    </div>
  );
};

export default ContextCalendar;
